import React, { useEffect, useState } from 'react';
import Navbar from '../components/resuableComponents/Navbar/Navbar';
import Pricing from '../components/resuableComponents/Pricing';
import FAQ from './FAQ';
import Footer from '../components/resuableComponents/Footer';
import { Container } from '@mui/material';
import { Link } from 'react-router-dom';

const INDUSTRY_REVIEWS = {
	restaurant: {
		label: '🍕 Restaurant & Cafe',
		title: 'Artisan Cafe & Bistro',
		tags: ['Delicious Food', 'Great Ambiance', 'Attentive Service', 'Must Visit'],
		review:
			'Amazing dining experience! The food was fresh, piping hot, and full of flavor. The staff made us feel right at home with exceptionally quick service. Highly recommend the chef’s special!',
	},
	clinic: {
		label: '🩺 Doctor & Clinic',
		title: 'CareWell Dental & Medical',
		tags: ['Painless Treatment', 'Friendly Doctors', 'Hygienic Setup', 'Caring Staff'],
		review:
			'Extremely satisfied with the consultation! Dr. Sharma explained everything patiently and the clinic is spotless and state-of-the-art. Zero wait time and gentle care throughout.',
	},
	salon: {
		label: '💇 Salon & Spa',
		title: 'Luxe Hair & Beauty Lounge',
		tags: ['Talented Stylists', 'Relaxing Spa', 'Top Products', 'Loved The Look'],
		review:
			'Hands down the best salon in town! My hair styling turned out exactly how I wanted. Courteous staff, premium products, and very relaxing atmosphere. Will definitely come back!',
	},
	retail: {
		label: '🛍️ Retail & Supermarket',
		title: 'Urban Trendz Fashion',
		tags: ['Huge Variety', 'Fair Pricing', 'Helpful Staff', 'Quick Billing'],
		review:
			'Wonderful shopping destination! Great selection of trendy designs and high quality fabrics. The staff helped me find my exact fit within minutes. Smooth checkout process!',
	},
	hotel: {
		label: '🏨 Hotel & Resort',
		title: 'Royal Palm Suites',
		tags: ['Luxurious Rooms', 'Scenic View', 'Delicious Breakfast', 'Hospitality'],
		review:
			'A memorable weekend stay! The rooms were spacious, spotless, and provided breathtaking views. The complimentary breakfast spread was lavish and the concierge was always prompt.',
	},
	garage: {
		label: '🚗 Auto Garage & Detailing',
		title: 'SpeedX Automotive Care',
		tags: ['Transparent Pricing', 'Genuine Parts', 'Timely Delivery', 'Expert Mechanics'],
		review:
			'Top-notch car detailing and routine service! Thorough diagnostic check, 100% genuine parts, and delivered on time without any surprise charges. My car feels brand new!',
	},
};

const CARD_FINISHES = [
	{
		id: 'black',
		name: 'Matte Obsidian Black',
		badge: 'Best Seller',
		gradient: 'from-slate-900 via-[#111827] to-slate-950',
		textColor: 'text-white',
		accentColor: 'text-amber-400',
		chipColor: 'bg-amber-400/20 text-amber-300 border-amber-400/30',
		tagline: 'Ultra-luxurious matte PVC with gold accents',
	},
	{
		id: 'white',
		name: 'Executive Pearl White',
		badge: 'Clean & Crisp',
		gradient: 'from-white via-slate-50 to-slate-100',
		textColor: 'text-slate-900',
		accentColor: 'text-blue-600',
		chipColor: 'bg-blue-50 text-blue-700 border-blue-200',
		tagline: 'Sleek porcelain white with embossed Google colors',
	},
	{
		id: 'standee',
		name: 'Acrylic Counter Standee',
		badge: 'Desktop Display',
		gradient: 'from-slate-800 via-slate-900 to-indigo-950',
		textColor: 'text-white',
		accentColor: 'text-emerald-400',
		chipColor: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30',
		tagline: 'Free-standing L-shaped display for reception & dining tables',
	},
];

const LandingPage = () => {
	// Selected Card Finish for 3D Preview
	const [activeFinish, setActiveFinish] = useState('black');
	const [isTapping, setIsTapping] = useState(false);

	// Interactive Industry Sandbox State
	const [selectedIndustry, setSelectedIndustry] = useState('restaurant');
	const [isGenerating, setIsGenerating] = useState(false);
	const [activeReviewText, setActiveReviewText] = useState(INDUSTRY_REVIEWS.restaurant.review);
	const [copied, setCopied] = useState(false);

	// Interactive Simulator State (Hero)
	const [simulatedRating, setSimulatedRating] = useState(5);

	// ROI Calculator State
	const [dailyCustomers, setDailyCustomers] = useState(45);
	const [avgTicket, setAvgTicket] = useState(600);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	// Handle simulated NFC Tap
	const handleSimulateTap = () => {
		setIsTapping(true);
		setTimeout(() => setIsTapping(false), 1200);
	};

	// Handle switching industry AI preview
	const handleSelectIndustry = (key) => {
		setSelectedIndustry(key);
		setIsGenerating(true);
		setCopied(false);
		setTimeout(() => {
			setActiveReviewText(INDUSTRY_REVIEWS[key].review);
			setIsGenerating(false);
		}, 300);
	};

	const handleCopyReview = () => {
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	// ROI calculations
	const extraReviewsPerMonth = Math.round(dailyCustomers * 30 * 0.12);
	const estimatedGoogleRankBoost = Math.min(95, Math.round(extraReviewsPerMonth * 0.8) + 35);
	const extraMonthlyRevenue = Math.round(extraReviewsPerMonth * avgTicket * 0.4);

	const currentFinish = CARD_FINISHES.find((f) => f.id === activeFinish) || CARD_FINISHES[0];

	return (
		<div className='min-h-screen bg-[#F8FAFC] text-slate-800 antialiased selection:bg-blue-600 selection:text-white'>
			{/* Simple, Clutter-Free Master Navbar */}
			<Navbar />

			{/* ========================================================================= */}
			{/* 1. HERO SECTION (High-End Clean Light SaaS)                                */}
			{/* ========================================================================= */}
			<section className='relative overflow-hidden pt-12 pb-20 lg:pt-16 lg:pb-28 bg-gradient-to-b from-blue-50/50 via-white to-[#F8FAFC] border-b border-slate-200/60'>
				<Container maxWidth='lg'>
					<div className='grid grid-cols-1 lg:grid-cols-12 gap-12 items-center'>
						{/* Left: Value Proposition */}
						<div className='lg:col-span-7 text-center lg:text-left space-y-6'>
							{/* Trust Badge */}
							<div className='inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold bg-white text-blue-700 border border-blue-200/80 shadow-sm'>
								<span className='w-2 h-2 rounded-full bg-emerald-500 animate-pulse'></span>
								<span>India's #1 NFC & QR Smart Review Platform</span>
								<span className='text-slate-300'>|</span>
								<span className='text-amber-500 font-extrabold'>⭐⭐⭐⭐⭐ 4.9/5</span>
							</div>

							{/* Catchy Headline */}
							<h1 className='text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight leading-[1.12]'>
								Turn Walk-In Customers Into{' '}
								<span className='bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 bg-clip-text text-transparent'>
									5-Star Google Reviews
								</span>{' '}
								With 1 Simple Tap
							</h1>

							{/* Clear Subtitle */}
							<p className='text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto lg:mx-0 font-normal'>
								Place contactless NFC smart review cards on your counter or table. Delighted customers tap their phone, our built-in AI drafts authentic positive reviews in seconds, and negative reviews are shielded privately.
							</p>

							{/* Checklist */}
							<div className='grid grid-cols-2 sm:grid-cols-3 gap-3 pt-1 text-xs sm:text-sm text-slate-700 font-semibold'>
								<div className='flex items-center gap-2 justify-center lg:justify-start'>
									<span className='text-emerald-600 font-bold'>✓</span>
									<span>No App Downloads</span>
								</div>
								<div className='flex items-center gap-2 justify-center lg:justify-start'>
									<span className='text-emerald-600 font-bold'>✓</span>
									<span>1-Click AI Review Drafter</span>
								</div>
								<div className='flex items-center gap-2 justify-center lg:justify-start'>
									<span className='text-emerald-600 font-bold'>✓</span>
									<span>Negative Review Shield</span>
								</div>
								<div className='flex items-center gap-2 justify-center lg:justify-start'>
									<span className='text-emerald-600 font-bold'>✓</span>
									<span>Zero Monthly Fees</span>
								</div>
								<div className='flex items-center gap-2 justify-center lg:justify-start'>
									<span className='text-emerald-600 font-bold'>✓</span>
									<span>Works on iOS & Android</span>
								</div>
								<div className='flex items-center gap-2 justify-center lg:justify-start'>
									<span className='text-emerald-600 font-bold'>✓</span>
									<span>Free Express Pan-India Courier</span>
								</div>
							</div>

							{/* Action Buttons */}
							<div className='pt-3 flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start'>
								<Link
									to='/pricing'
									className='w-full sm:w-auto px-8 py-3.5 rounded-xl text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-md shadow-blue-600/25 transition-all text-center transform hover:-translate-y-0.5'>
									Order Smart Cards (From ₹999) →
								</Link>

								<a
									href='#ai-sandbox'
									className='w-full sm:w-auto px-6 py-3.5 rounded-xl text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-300 shadow-sm transition-all text-center'>
									⚡ Test AI Review Generator
								</a>
							</div>

							{/* Trust Line */}
							<div className='pt-2 flex flex-wrap items-center justify-center lg:justify-start gap-4 text-xs text-slate-500'>
								<span className='flex items-center gap-1.5 font-semibold text-slate-700'>
									<span className='text-emerald-600 font-bold'>●</span> Trusted by 1,200+ Businesses in India
								</span>
								<span>•</span>
								<span>Dispatched in 24h via Express Air</span>
							</div>
						</div>

						{/* Right: Photorealistic Interactive 3D Card & Phone Demo */}
						<div className='lg:col-span-5 flex justify-center'>
							<div className='relative w-full max-w-md'>
								{/* Card Finish Switcher */}
								<div className='flex items-center justify-center gap-2 mb-3'>
									{CARD_FINISHES.map((f) => (
										<button
											key={f.id}
											onClick={() => setActiveFinish(f.id)}
											className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
												activeFinish === f.id
													? 'bg-blue-600 text-white shadow-sm'
													: 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
											}`}>
											{f.name.split(' ')[1] || f.name}
										</button>
									))}
								</div>

								{/* Main Preview Container */}
								<div className='bg-white rounded-3xl p-6 sm:p-7 card-shadow border border-slate-200/80 space-y-5 relative'>
									{/* Top Bar of Preview */}
									<div className='flex items-center justify-between border-b border-slate-100 pb-3'>
										<div>
											<span className='text-xs font-bold text-slate-900'>{currentFinish.name}</span>
											<p className='text-[10px] text-slate-400'>{currentFinish.tagline}</p>
										</div>
										<button
											onClick={handleSimulateTap}
											className='px-3 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-colors flex items-center gap-1'>
											<span>📶 Tap Card</span>
										</button>
									</div>

									{/* 3D Realistic Card Rendering */}
									<div
										onClick={handleSimulateTap}
										className={`cursor-pointer rounded-2xl p-6 shadow-xl relative overflow-hidden bg-gradient-to-br ${currentFinish.gradient} ${
											activeFinish === 'white' ? 'border border-slate-300' : 'border border-slate-700'
										} transition-all duration-300 ${isTapping ? 'scale-95 ring-4 ring-blue-500' : 'hover:scale-[1.01]'}`}>
										{/* Glass sheen */}
										<div className='absolute top-0 right-0 w-36 h-full bg-white/10 skew-x-12 pointer-events-none'></div>

										{/* Header inside card */}
										<div className='flex items-center justify-between'>
											<span className={`text-base font-black tracking-wider ${currentFinish.textColor}`}>
												ALPHAREVIEW<span className='text-blue-500'>.IN</span>
											</span>
											<div className='flex items-center gap-1.5'>
												<span className='text-lg'>📶</span>
												<span className='text-[10px] uppercase font-bold tracking-wider opacity-75'>
													NFC Smart
												</span>
											</div>
										</div>

										{/* Card Body & Gold Chip */}
										<div className='mt-8 flex items-end justify-between'>
											<div>
												<div className='w-9 h-7 rounded bg-gradient-to-br from-amber-300 to-amber-500 border border-amber-600 shadow-sm flex items-center justify-center text-[10px] font-bold text-amber-950 mb-3'>
													칩
												</div>
												<p className={`text-[10px] font-bold uppercase tracking-wider ${currentFinish.accentColor}`}>
													Tap to Leave 5-Star Review
												</p>
												<p className={`text-sm font-extrabold mt-0.5 ${currentFinish.textColor}`}>
													Your Business Name Here
												</p>
												<div className='flex items-center gap-1 text-amber-400 mt-1 text-xs font-bold'>
													★★★★★ <span className={`text-[11px] ml-1 opacity-80 ${currentFinish.textColor}`}>5.0 on Google</span>
												</div>
											</div>

											{/* High-Resolution QR */}
											<div className='w-14 h-14 bg-white rounded-xl p-1.5 shadow-md flex flex-col justify-between items-center border border-slate-200'>
												<div className='grid grid-cols-3 gap-0.5 w-full h-full'>
													<div className='bg-slate-900 rounded-sm'></div>
													<div className='bg-slate-300 rounded-sm'></div>
													<div className='bg-slate-900 rounded-sm'></div>
													<div className='bg-slate-300 rounded-sm'></div>
													<div className='bg-blue-600 rounded-sm'></div>
													<div className='bg-slate-300 rounded-sm'></div>
													<div className='bg-slate-900 rounded-sm'></div>
													<div className='bg-slate-300 rounded-sm'></div>
													<div className='bg-slate-900 rounded-sm'></div>
												</div>
											</div>
										</div>
									</div>

									{/* Simulated Phone Popup on Tap */}
									<div className='bg-slate-50 rounded-2xl p-4 border border-slate-200 space-y-3'>
										<div className='flex items-center justify-between text-xs text-slate-500 font-semibold'>
											<span>📱 Customer's Smartphone Screen</span>
											<span className='text-emerald-600 font-bold'>
												{isTapping ? '⚡ Reading NFC...' : '● Instant 1-Tap Ready'}
											</span>
										</div>

										{/* Star rating selector */}
										<div className='flex items-center justify-between bg-white px-3 py-2 rounded-xl border border-slate-200'>
											<span className='text-xs font-semibold text-slate-700'>Customer Rating:</span>
											<div className='flex items-center gap-1'>
												{[1, 2, 3, 4, 5].map((s) => (
													<button
														key={s}
														onClick={() => setSimulatedRating(s)}
														className={`text-lg transition-transform hover:scale-125 focus:outline-none ${
															s <= simulatedRating ? 'text-amber-400' : 'text-slate-300'
														}`}>
														★
													</button>
												))}
											</div>
										</div>

										{/* Sentiment Routing Preview */}
										{simulatedRating >= 4 ? (
											<div className='bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs space-y-2'>
												<div className='flex items-center justify-between text-emerald-800 font-bold'>
													<span>🎉 5-Star Delighted Customer</span>
													<span className='text-emerald-700'>AI Draft Ready</span>
												</div>
												<p className='text-slate-600 text-[11px] leading-relaxed'>
													"Exceptional experience! Courteous staff, super speedy service, and top quality. Highly recommend!"
												</p>
												<a
													href='/review/40/Alpha+Connects'
													target='_blank'
													rel='noopener noreferrer'
													className='block w-full py-2 text-center rounded-lg text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-sm'>
													Try Live Demo in Browser →
												</a>
											</div>
										) : (
											<div className='bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs space-y-1.5'>
												<div className='flex items-center justify-between text-amber-800 font-bold'>
													<span>🛡️ Private Feedback Shield</span>
													<span className='text-amber-700'>Public Safe</span>
												</div>
												<p className='text-slate-600 text-[11px] leading-relaxed'>
													Customer grievance is captured privately in your merchant dashboard, protecting your public Google rating!
												</p>
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				</Container>
			</section>

			{/* ========================================================================= */}
			{/* 2. PLATFORMS & COMPATIBILITY TICKER                                      */}
			{/* ========================================================================= */}
			<section className='py-8 bg-white border-b border-slate-200'>
				<Container maxWidth='lg'>
					<div className='flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left'>
						<div>
							<p className='text-xs font-bold uppercase tracking-wider text-slate-400'>
								Direct Review Redirection Support
							</p>
							<p className='text-sm font-semibold text-slate-800'>
								Connects with all leading consumer review channels
							</p>
						</div>

						<div className='flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm font-bold text-slate-700'>
							<span className='flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg'>
								<span className='text-blue-500 font-black'>G</span> Google Business
							</span>
							<span className='flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg'>
								<span className='text-emerald-500'>🦉</span> TripAdvisor
							</span>
							<span className='flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg'>
								<span className='text-red-500'>🔴</span> Zomato
							</span>
							<span className='flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg'>
								<span className='text-emerald-600'>⭐</span> Trustpilot
							</span>
							<span className='flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg'>
								<span className='text-blue-600'>f</span> Facebook
							</span>
						</div>
					</div>
				</Container>
			</section>

			{/* ========================================================================= */}
			{/* 3. INTERACTIVE AI REVIEW ASSISTANT SANDBOX                                */}
			{/* ========================================================================= */}
			<section id='ai-sandbox' className='py-20 bg-slate-50 border-b border-slate-200'>
				<Container maxWidth='lg'>
					<div className='text-center max-w-3xl mx-auto mb-12'>
						<span className='inline-flex items-center px-3.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200 mb-3'>
							Interactive AI Assistant
						</span>
						<h2 className='text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight'>
							See How Alpha AI Eliminates Writer's Block
						</h2>
						<p className='text-slate-600 text-base sm:text-lg mt-3'>
							Customers often don't know what to write. Select your industry below and see how Alpha AI drafts an articulate 5-star review in 1 second.
						</p>
					</div>

					<div className='max-w-4xl mx-auto bg-white rounded-3xl p-6 sm:p-8 card-shadow border border-slate-200'>
						{/* Industry Selection Buttons */}
						<div className='flex flex-wrap gap-2 justify-center mb-6'>
							{Object.entries(INDUSTRY_REVIEWS).map(([key, data]) => (
								<button
									key={key}
									onClick={() => handleSelectIndustry(key)}
									className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
										selectedIndustry === key
											? 'bg-blue-600 text-white shadow-sm'
											: 'bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200'
									}`}>
									{data.label}
								</button>
							))}
						</div>

						{/* Output Card */}
						<div className='bg-slate-50 rounded-2xl p-6 sm:p-7 border border-slate-200 space-y-4'>
							<div className='flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200 pb-3'>
								<div>
									<span className='text-xs font-bold text-blue-600 uppercase tracking-wider'>Selected Industry</span>
									<h3 className='text-lg font-bold text-slate-900 mt-0.5'>
										{INDUSTRY_REVIEWS[selectedIndustry].title}
									</h3>
								</div>
								<div className='text-amber-500 text-sm font-bold bg-amber-50 px-3 py-1 rounded-full border border-amber-200 self-start sm:self-auto'>
									★★★★★ 5.0 (AI Crafted)
								</div>
							</div>

							{/* Feedback Tags */}
							<div>
								<p className='text-xs font-bold text-slate-500 uppercase tracking-wider mb-2'>Customer Tags Highlighted:</p>
								<div className='flex flex-wrap gap-1.5'>
									{INDUSTRY_REVIEWS[selectedIndustry].tags.map((tag, idx) => (
										<span
											key={idx}
											className='px-2.5 py-1 rounded-md text-xs font-medium bg-white text-slate-700 border border-slate-200'>
											✓ {tag}
										</span>
									))}
								</div>
							</div>

							{/* AI Review Copy Box */}
							<div className='bg-white rounded-xl p-4 border border-slate-200'>
								<div className='flex items-center justify-between text-xs text-slate-500 mb-1.5'>
									<span className='font-bold text-emerald-600 flex items-center gap-1.5'>
										<span className='w-1.5 h-1.5 rounded-full bg-emerald-500'></span>
										AI Generated Review Copy
									</span>
									<span className='font-mono text-slate-400'>1-Click Copy</span>
								</div>

								{isGenerating ? (
									<div className='py-4 text-center text-sm text-slate-400 animate-pulse'>
										Generating authentic 5-star review draft...
									</div>
								) : (
									<p className='text-slate-800 text-sm sm:text-base leading-relaxed italic'>
										"{activeReviewText}"
									</p>
								)}
							</div>

							{/* Actions */}
							<div className='flex flex-col sm:flex-row gap-3 pt-2'>
								<button
									onClick={handleCopyReview}
									className='flex-1 py-3 px-5 rounded-xl font-bold text-xs uppercase tracking-wider bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-all'>
									{copied ? '✓ Copied to Clipboard!' : '📋 Copy Review Draft'}
								</button>
								<a
									href='/review/40/Alpha+Connects'
									target='_blank'
									rel='noopener noreferrer'
									className='flex-1 py-3 px-5 rounded-xl font-bold text-xs uppercase tracking-wider bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-all text-center'>
									🚀 Test Live Review Web App
								</a>
							</div>
						</div>
					</div>
				</Container>
			</section>

			{/* ========================================================================= */}
			{/* 4. BEFORE VS AFTER COMPARISON                                             */}
			{/* ========================================================================= */}
			<section className='py-20 bg-white border-b border-slate-200'>
				<Container maxWidth='lg'>
					<div className='text-center max-w-3xl mx-auto mb-14'>
						<span className='inline-flex items-center px-3.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-slate-100 text-slate-700 mb-3'>
							Proven Results
						</span>
						<h2 className='text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight'>
							Without AlphaReview vs. With AlphaReview
						</h2>
					</div>

					<div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
						{/* Before */}
						<div className='bg-rose-50/40 rounded-3xl p-8 border border-rose-200 shadow-sm space-y-4'>
							<div className='flex items-center justify-between border-b border-rose-100 pb-3'>
								<span className='text-xs font-extrabold uppercase tracking-wider text-rose-600'>
									❌ Without AlphaReview
								</span>
								<span className='text-xs text-rose-700 font-bold bg-rose-100 px-2.5 py-0.5 rounded-full'>
									Average 3.6★ Rating
								</span>
							</div>

							<div className='space-y-3.5 text-sm text-slate-700 pt-2'>
								<div className='flex items-start gap-2.5'>
									<span className='text-rose-500 font-bold'>✗</span>
									<span>Happy customers leave without reviewing because searching Google takes too much effort.</span>
								</div>
								<div className='flex items-start gap-2.5'>
									<span className='text-rose-500 font-bold'>✗</span>
									<span>Only frustrated customers leave public reviews, hurting your rating and reputation.</span>
								</div>
								<div className='flex items-start gap-2.5'>
									<span className='text-rose-500 font-bold'>✗</span>
									<span>Paper printed QR codes look cheap, get ignored, and don't guide customers on what to say.</span>
								</div>
								<div className='flex items-start gap-2.5'>
									<span className='text-rose-500 font-bold'>✗</span>
									<span>Competitors with higher ratings dominate Google "Near Me" search results, stealing walk-ins.</span>
								</div>
							</div>
						</div>

						{/* After */}
						<div className='bg-emerald-50/40 rounded-3xl p-8 border-2 border-emerald-500 shadow-md space-y-4'>
							<div className='flex items-center justify-between border-b border-emerald-100 pb-3'>
								<span className='text-xs font-extrabold uppercase tracking-wider text-emerald-600'>
									✅ With AlphaReview
								</span>
								<span className='text-xs text-emerald-700 font-bold bg-emerald-100 px-2.5 py-0.5 rounded-full'>
									4.9★ Top Rank Rating
								</span>
							</div>

							<div className='space-y-3.5 text-sm text-slate-800 pt-2'>
								<div className='flex items-start gap-2.5'>
									<span className='text-emerald-600 font-bold text-base'>✓</span>
									<span>Customers tap their phone in 2 seconds while paying the bill or checking out at the desk.</span>
								</div>
								<div className='flex items-start gap-2.5'>
									<span className='text-emerald-600 font-bold text-base'>✓</span>
									<span>Alpha AI drafts genuine, glowing reviews with 1 click, eliminating writer’s block.</span>
								</div>
								<div className='flex items-start gap-2.5'>
									<span className='text-emerald-600 font-bold text-base'>✓</span>
									<span>Negative reviews are captured privately in your dashboard, preserving your public rating.</span>
								</div>
								<div className='flex items-start gap-2.5'>
									<span className='text-emerald-600 font-bold text-base'>✓</span>
									<span>Your Google Maps ranking shoots to #1 locally, driving a steady stream of new customers.</span>
								</div>
							</div>
						</div>
					</div>
				</Container>
			</section>

			{/* ========================================================================= */}
			{/* 5. THE 3-STEP PROCESS                                                     */}
			{/* ========================================================================= */}
			<section id='how-it-works' className='py-20 bg-slate-50 border-b border-slate-200'>
				<Container maxWidth='lg'>
					<div className='text-center max-w-3xl mx-auto mb-14'>
						<span className='inline-flex items-center px-3.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200 mb-3'>
							Simple 3 Steps
						</span>
						<h2 className='text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight'>
							How AlphaReview Works
						</h2>
					</div>

					<div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
						<div className='bg-white rounded-2xl p-7 border border-slate-200 card-shadow'>
							<div className='w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center font-bold text-lg mb-4'>
								1
							</div>
							<h3 className='text-lg font-bold text-slate-900 mb-1.5'>Contactless Tap or Scan</h3>
							<p className='text-sm text-slate-600 leading-relaxed'>
								Customer holds their iPhone or Android phone near the card or scans the dynamic QR. No apps to install.
							</p>
						</div>

						<div className='bg-white rounded-2xl p-7 border border-slate-200 card-shadow'>
							<div className='w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center font-bold text-lg mb-4'>
								2
							</div>
							<h3 className='text-lg font-bold text-slate-900 mb-1.5'>Alpha AI Creates Review</h3>
							<p className='text-sm text-slate-600 leading-relaxed'>
								Customer picks tags like "Friendly Staff" or "Great Service". Our AI generates a tailored review draft in 1 second.
							</p>
						</div>

						<div className='bg-white rounded-2xl p-7 border border-slate-200 card-shadow'>
							<div className='w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center font-bold text-lg mb-4'>
								3
							</div>
							<h3 className='text-lg font-bold text-slate-900 mb-1.5'>Published on Google</h3>
							<p className='text-sm text-slate-600 leading-relaxed'>
								The customer taps 1 button to paste and submit directly on Google Maps. Unhappy ratings are shielded privately.
							</p>
						</div>
					</div>
				</Container>
			</section>

			{/* ========================================================================= */}
			{/* 6. INTERACTIVE ROI CALCULATOR                                             */}
			{/* ========================================================================= */}
			<section id='calculator' className='py-20 bg-white border-b border-slate-200'>
				<Container maxWidth='lg'>
					<div className='text-center max-w-3xl mx-auto mb-14'>
						<span className='inline-flex items-center px-3.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 mb-3'>
							Interactive Calculator
						</span>
						<h2 className='text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight'>
							Calculate Your Review & Revenue Surge
						</h2>
					</div>

					<div className='max-w-4xl mx-auto bg-slate-50 rounded-3xl p-6 sm:p-9 border border-slate-200 card-shadow'>
						<div className='grid grid-cols-1 md:grid-cols-2 gap-8 items-center'>
							<div className='space-y-6'>
								<div>
									<div className='flex justify-between items-center mb-2'>
										<label className='text-xs font-bold uppercase tracking-wider text-slate-700'>
											Daily In-Person Customers:
										</label>
										<span className='text-blue-600 font-extrabold text-lg'>
											{dailyCustomers} / day
										</span>
									</div>
									<input
										type='range'
										min='10'
										max='200'
										step='5'
										value={dailyCustomers}
										onChange={(e) => setDailyCustomers(Number(e.target.value))}
										className='w-full h-2.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600'
									/>
								</div>

								<div>
									<div className='flex justify-between items-center mb-2'>
										<label className='text-xs font-bold uppercase tracking-wider text-slate-700'>
											Average Customer Spending:
										</label>
										<span className='text-emerald-600 font-extrabold text-lg'>
											₹{avgTicket}
										</span>
									</div>
									<input
										type='range'
										min='100'
										max='3000'
										step='50'
										value={avgTicket}
										onChange={(e) => setAvgTicket(Number(e.target.value))}
										className='w-full h-2.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-600'
									/>
								</div>
							</div>

							<div className='bg-white rounded-2xl p-6 border border-slate-200 flex flex-col justify-between shadow-sm'>
								<div>
									<p className='text-xs font-bold uppercase tracking-wider text-slate-500'>
										Projected Monthly Results:
									</p>
									<div className='mt-3 flex items-baseline gap-2'>
										<span className='text-4xl sm:text-5xl font-extrabold text-blue-600'>
											+{extraReviewsPerMonth}
										</span>
										<span className='text-sm text-slate-700 font-bold'>
											New 5-Star Reviews / mo
										</span>
									</div>
									<p className='text-xs text-slate-500 mt-1'>
										Google Maps search boost: <strong className='text-emerald-600'>+{estimatedGoogleRankBoost}%</strong>
									</p>
								</div>

								<div className='mt-6 pt-4 border-t border-slate-100'>
									<p className='text-xs text-slate-500 uppercase tracking-wider font-bold'>
										Estimated Additional Monthly Revenue:
									</p>
									<p className='text-3xl font-extrabold text-emerald-600 mt-1'>
										₹{extraMonthlyRevenue.toLocaleString('en-IN')}
									</p>
								</div>
							</div>
						</div>

						<div className='mt-8 pt-6 border-t border-slate-200 text-center'>
							<Link
								to='/pricing'
								className='inline-flex items-center gap-2 px-8 py-3.5 rounded-xl text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 transition-all shadow-sm'>
								<span>Order Your Smart Review Cards Now →</span>
							</Link>
						</div>
					</div>
				</Container>
			</section>

			{/* ========================================================================= */}
			{/* 7. FEATURES SHOWCASE GRID                                                */}
			{/* ========================================================================= */}
			<section id='features' className='py-20 bg-slate-50 border-b border-slate-200'>
				<Container maxWidth='lg'>
					<div className='text-center max-w-3xl mx-auto mb-14'>
						<span className='inline-flex items-center px-3.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200 mb-3'>
							Features
						</span>
						<h2 className='text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight'>
							Everything You Need To Grow 5-Star Reviews
						</h2>
					</div>

					<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
						<div className='p-6 rounded-2xl bg-white border border-slate-200 card-shadow'>
							<span className='text-2xl'>📶</span>
							<h3 className='font-bold text-slate-900 mt-3 text-base'>High-Frequency NFC</h3>
							<p className='text-xs text-slate-600 mt-1.5 leading-relaxed'>
								NTAG contactless chip for lightning-fast native reading on all iPhone & Android devices.
							</p>
						</div>

						<div className='p-6 rounded-2xl bg-white border border-slate-200 card-shadow'>
							<span className='text-2xl'>📱</span>
							<h3 className='font-bold text-slate-900 mt-3 text-base'>Dynamic QR Backup</h3>
							<p className='text-xs text-slate-600 mt-1.5 leading-relaxed'>
								Guarantees 100% compatibility so older phones or budget cameras scan without issues.
							</p>
						</div>

						<div className='p-6 rounded-2xl bg-white border border-slate-200 card-shadow'>
							<span className='text-2xl'>🛡️</span>
							<h3 className='font-bold text-slate-900 mt-3 text-base'>Negative Review Shield</h3>
							<p className='text-xs text-slate-600 mt-1.5 leading-relaxed'>
								Diverts 1-3 star grievances away from Google and into your private management portal.
							</p>
						</div>

						<div className='p-6 rounded-2xl bg-white border border-slate-200 card-shadow'>
							<span className='text-2xl'>🤖</span>
							<h3 className='font-bold text-slate-900 mt-3 text-base'>Alpha AI Drafter</h3>
							<p className='text-xs text-slate-600 mt-1.5 leading-relaxed'>
								Generates genuine, fluent reviews with keyword recommendations in under one second.
							</p>
						</div>

						<div className='p-6 rounded-2xl bg-white border border-slate-200 card-shadow'>
							<span className='text-2xl'>🌐</span>
							<h3 className='font-bold text-slate-900 mt-3 text-base'>Multi-Platform Support</h3>
							<p className='text-xs text-slate-600 mt-1.5 leading-relaxed'>
								Route reviews to Google Maps, TripAdvisor, Zomato, Trustpilot, or custom URLs.
							</p>
						</div>

						<div className='p-6 rounded-2xl bg-white border border-slate-200 card-shadow'>
							<span className='text-2xl'>📊</span>
							<h3 className='font-bold text-slate-900 mt-3 text-base'>Real-Time Analytics</h3>
							<p className='text-xs text-slate-600 mt-1.5 leading-relaxed'>
								Track daily scan counts, tap conversions, and staff performance from your merchant portal.
							</p>
						</div>

						<div className='p-6 rounded-2xl bg-white border border-slate-200 card-shadow'>
							<span className='text-2xl'>💎</span>
							<h3 className='font-bold text-slate-900 mt-3 text-base'>Waterproof PVC Cards</h3>
							<p className='text-xs text-slate-600 mt-1.5 leading-relaxed'>
								Industrial-grade durable PVC withstands spills, grease, and years of customer handling.
							</p>
						</div>

						<div className='p-6 rounded-2xl bg-white border border-slate-200 card-shadow'>
							<span className='text-2xl'>💰</span>
							<h3 className='font-bold text-slate-900 mt-3 text-base'>Zero Monthly Subscriptions</h3>
							<p className='text-xs text-slate-600 mt-1.5 leading-relaxed'>
								One-time card purchase with lifetime access, unlimited taps, and zero recurring invoices.
							</p>
						</div>
					</div>
				</Container>
			</section>

			{/* ========================================================================= */}
			{/* 8. PRICING SECTION                                                        */}
			{/* ========================================================================= */}
			<Pricing />

			{/* ========================================================================= */}
			{/* 9. FAQ SECTION                                                            */}
			{/* ========================================================================= */}
			<FAQ />

			{/* ========================================================================= */}
			{/* 10. FINAL CTA BANNER                                                      */}
			{/* ========================================================================= */}
			<section className='py-20 bg-blue-600 text-white text-center'>
				<Container maxWidth='md'>
					<h2 className='text-3xl sm:text-4xl font-extrabold tracking-tight'>
						Ready To Scale Your 5-Star Google Reviews?
					</h2>
					<p className='text-base sm:text-lg text-blue-100 mt-3 leading-relaxed'>
						Join 1,200+ local businesses that grow customer reputation with zero monthly fees. Free Pan-India express delivery included.
					</p>

					<div className='mt-8 flex flex-col sm:flex-row items-center justify-center gap-4'>
						<Link
							to='/pricing'
							className='w-full sm:w-auto px-8 py-3.5 rounded-xl text-sm font-bold text-blue-900 bg-white hover:bg-slate-100 shadow-md transition-all'>
							Order Smart Cards (From ₹999) →
						</Link>
						<a
							href='https://wa.me/919880889303'
							target='_blank'
							rel='noopener noreferrer'
							className='w-full sm:w-auto px-6 py-3.5 rounded-xl text-sm font-semibold text-white bg-blue-700 hover:bg-blue-800 transition-all flex items-center justify-center gap-2'>
							<i className='fa fa-whatsapp text-lg'></i>
							<span>Chat on WhatsApp (+91 98808 89303)</span>
						</a>
					</div>
				</Container>
			</section>

			{/* ========================================================================= */}
			{/* 11. STICKY MOBILE BOTTOM BAR                                              */}
			{/* ========================================================================= */}
			<div className='md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200 px-4 py-2.5 flex items-center justify-between gap-3 shadow-lg'>
				<a
					href='https://wa.me/919880889303'
					target='_blank'
					rel='noopener noreferrer'
					className='p-2.5 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center justify-center'>
					<i className='fa fa-whatsapp text-xl text-emerald-600'></i>
				</a>
				<Link
					to='/pricing'
					className='flex-1 py-3 px-4 rounded-xl font-bold text-sm text-center text-white bg-blue-600 shadow-sm'>
					Order Cards • From ₹999
				</Link>
			</div>

			{/* Footer */}
			<Footer />
		</div>
	);
};

export default LandingPage;
